import { getViewerType } from "lib/actions/assets/getViewerType";
import { popToastError } from "lib/actions/toast";
import { fetchApi } from "lib/actions/fetcher";
import { endpointsAssets } from "lib/actions/endpoints/endpointsAssets";
import { usePdfTron } from "lib/contexts/ProjectContexts/PdfTronContext";
import { useCesium } from "../../../components/elements/CesiumViewer/core/useCesium";
import { useSyncCesium } from "components/elements/CesiumViewer/core/useSyncCesium";
import { isAssetVisible } from "components/elements/CesiumViewer/core/wrapper/AssetsPlugin";
import {
  useSelectedViewer,
  KeyframeViewer,
} from "lib/contexts/ProjectContexts/SelectedViewerContext";
import { Guard } from "lib/actions/util";
import { useViewsMode } from "lib/contexts/SpatialContexts/ViewsModeContext";
import { AssetVersionWithAssetModel } from "lib/types/assets/AssetVersionWithAssetModel";
import { RenderableAsset } from "lib/types/assets/RenderableAsset";

export function useAssetsControls() {
  const { assetsPlugin } = useCesium();
  const viewerControls = useSelectedViewer();
  const pdfTronControls = usePdfTron();
  const cesiumAssets = useSyncCesium(assetsPlugin.assets);
  const { exitViewMode } = useViewsMode();

  function isShowingAsset(version: AssetVersionWithAssetModel) {
    Guard.require(version, "version");
    if (!version.contentUri) return false;
    const viewerType = getViewerType(version.contentUri, version.asset.name);
    switch (viewerType) {
      case KeyframeViewer.Cesium:
        return cesiumAssets.get(version.contentUri)?.isShowing ?? false;
      case KeyframeViewer.PdfTron:
        return (
          viewerControls.viewer === KeyframeViewer.PdfTron &&
          pdfTronControls?.fileOptions?.versionId === version.id
        );
      case null:
        return false;
      default:
        throw new Error("Unsupported viewer type");
    }
  }

  /**
   * Whether the asset is rendered in the Cesium viewer at all, including when a
   * layer is what's keeping it visible. `isShowingAsset` deliberately ignores
   * layers because it drives the show/hide toggles, whose action only flips the
   * asset's own visibility.
   */
  function isVisibleAsset(version: AssetVersionWithAssetModel) {
    Guard.require(version, "version");
    if (!version.contentUri) return false;
    return isAssetVisible(cesiumAssets.get(version.contentUri));
  }

  async function showAsset(
    version: AssetVersionWithAssetModel,
  ): Promise<boolean> {
    Guard.require(version, "version");
    const viewerType = getViewerType(version.contentUri, version.asset.name);
    switch (viewerType) {
      case KeyframeViewer.Cesium: {
        viewerControls?.setViewer(KeyframeViewer.Cesium);
        try {
          await assetsPlugin.show(version);
          return true;
        } catch (error) {
          reportLoadFailure(version, error);
          return false;
        }
      }
      case KeyframeViewer.PdfTron: {
        if (!viewerControls || !pdfTronControls)
          throw new Error("Controls must be available to view a pdf.");
        viewerControls.setViewer(KeyframeViewer.PdfTron);
        pdfTronControls.setFileOptions({
          versionId: version.id,
          isLoadingUrl: true,
          fileUrl: null,
        });
        if (version.createdOn.isBefore("2024-10-01")) {
          popToastError(
            "File not available, it was uploaded before October 2024.",
          );
          return false;
        }
        let downloadEndpoint: { url: string } | null = null;
        try {
          const endpoint = endpointsAssets.versionDownloadUrl(version.id);
          downloadEndpoint = await fetchApi({
            url: endpoint.url,
            method: endpoint.method,
          }).then((res) => res.json() as Promise<{ url: string }>);
        } catch (err) {
          console.error(
            `Failed to get download url for asset content. AssetId: ${version.asset.id}, ContentUri: ${version.contentUri}`,
            err,
          );
        }
        if (!downloadEndpoint) {
          popToastError("An error occurred whilst loading the file.");
          return false;
        }
        pdfTronControls.setFileOptions((current) => {
          if (current?.versionId !== version.id) return current;
          return {
            versionId: version.id,
            isLoadingUrl: false,
            fileUrl: downloadEndpoint.url,
          };
        });
        return true;
      }
      case null:
        return false;
      default:
        throw new Error("Unsupported viewer type");
    }
  }

  function hideAsset(version: AssetVersionWithAssetModel) {
    Guard.require(version, "version");
    const viewerType = getViewerType(version.contentUri, version.asset.name);
    switch (viewerType) {
      case KeyframeViewer.Cesium:
        assetsPlugin.hide(version.contentUri);
        break;
      case KeyframeViewer.PdfTron:
        viewerControls?.setViewer(KeyframeViewer.Cesium);
        pdfTronControls?.setFileOptions(null);
        break;
      case null:
        return;
      default:
        throw new Error("Unsupported viewer type");
    }
  }

  async function toggleShowingAsset(version: AssetVersionWithAssetModel) {
    Guard.require(version, "version");
    const viewerType = getViewerType(version.contentUri, version.asset.name);
    switch (viewerType) {
      case KeyframeViewer.Cesium: {
        viewerControls?.setViewer(KeyframeViewer.Cesium);
        try {
          await assetsPlugin.toggleShowing(version);
        } catch (error) {
          reportLoadFailure(version, error);
        }
        break;
      }
      case KeyframeViewer.PdfTron: {
        if (pdfTronControls?.fileOptions?.versionId === version.id) {
          hideAsset(version);
        } else {
          await showAsset(version);
        }
        break;
      }
      case null:
        return;
      default:
        throw new Error("Unsupported viewer type");
    }
  }

  async function flyToAsset(version: RenderableAsset) {
    Guard.require(version, "version");
    const viewerType = getViewerType(version.contentUri, version.asset.name);
    if (viewerType === KeyframeViewer.Cesium) {
      viewerControls?.setViewer(KeyframeViewer.Cesium);
      // Flying elsewhere leaves any open view, without restoring the scene
      // from before it, since that would undo the flight.
      exitViewMode();
      try {
        await assetsPlugin.flyTo(version);
      } catch (error) {
        reportLoadFailure(version, error);
      }
    }
  }

  function reportLoadFailure(version: RenderableAsset, error: unknown) {
    console.error(
      `Failed to load asset content. AssetId: ${version.asset.id}, ContentUri: ${version.contentUri}`,
      error,
    );
    popToastError(
      "Asset content could not be loaded. Please contact support if this issue persists.",
    );
  }

  return {
    isShowingAsset,
    isVisibleAsset,
    showAsset,
    hideAsset,
    toggleShowingAsset,
    flyToAsset,
  };
}
